'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { ChevronRight, Lock, X as XIcon } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContentFullScreen,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/shadcn/drawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { NoticeBanner } from '@/shared/ui';
import { LegalDocumentPage } from '@/features/support/ui/LegalDocumentPage';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';
import type { PaywallAction } from '@/shared/lib/api';
import { PaywallBullets } from '@/features/payment/ui/PaywallBullets';
import { TokenPackageCard } from '@/features/payment/ui/TokenPackageCard';
import { useTokenPackages } from '@/shared/api/hooks/use-token-packages';
import { formatPackPrice } from '@/shared/lib/format-catalog-price';
import {
  findPackProduct,
  useStoreCatalog,
} from '@/shared/api/hooks/use-store-catalog';
import { usePasses } from '@/shared/api/hooks/use-passes';
import { PackPaywallContent } from './PackPaywallContent';

/**
 * Root-mounted dialog that renders whatever PaywallPayload the API put in
 * the current limit error. Everything (title, body, button labels) comes
 * from the server — no local copy. See apps/api/app/Dtos/PaywallPayload.php.
 *
 * `secondary_action.target` is a semantic route token (e.g. `project_angles`)
 * resolved to a concrete URL here. The button is hidden when the user is
 * already on the target screen so it doesn't duplicate Cancel.
 */
export function LimitPaywallDialog() {
  const router = useRouter();
  const pathname = usePathname();
  const tCommon = useTranslations('common');
  const payload = useLimitPaywallStore((s) => s.payload);
  const code = useLimitPaywallStore((s) => s.code);
  const meta = useLimitPaywallStore((s) => s.meta);
  const hidePaywall = useLimitPaywallStore((s) => s.hidePaywall);
  const openPassPaywall = usePassPaywallStore((s) => s.open);
  // Canonical Facade Pack offer copy — shown when the user taps "Get Facade
  // Pack" on a bulletless alert (the render-queue limit), so the pack body
  // carries the real pack pitch, not the alert's parallel-renders context line.
  const packRequiredPayload = usePaywallPayload('PACK_REQUIRED');

  // True once the user accepts a bulletless pack offer (queue limit): swap this
  // dialog from the small alert to the full pack body in place.
  const [showPack, setShowPack] = useState(false);

  // While the Paddle checkout overlay is up we relax this sheet to non-modal.
  // The modal sheet locks `<body>` to `pointer-events: none`; Paddle injects its
  // overlay iframe at `<body>` level, OUTSIDE this sheet's subtree, so under the
  // lock it renders but is dead to taps. Passing `modal={false}` to the vaul
  // Drawer clears that lock (vaul restores `body.style.pointerEvents = 'auto'`),
  // making the iframe clickable, and keeps the sheet mounted so it can show the
  // post-purchase success in place (parity with Apple/Android). Restored on
  // checkout.closed.
  //
  // INVARIANT: EVERY checkout launched from inside this sheet must flip
  // `checkoutActive` (pack via PackPaywallContent, token cards via
  // TokenPackageOffer below). A launch that skips it leaves the body locked and
  // the Paddle form untappable — that's exactly how the token path regressed.
  const [checkoutActive, setCheckoutActive] = useState(false);

  // Terms-of-service legal doc, opened in a nested dialog over the chooser
  // footer (parity with the Pass paywall + Apple/Android sheet footers).
  const [showTerms, setShowTerms] = useState(false);
  const tLegal = useTranslations('account');

  const projectId = pathname?.match(/^\/project\/([^/]+)/)?.[1];
  // Pack offers bind to the building the limit fired on (error meta) and fall
  // back to the current route's project (preflight-routed payloads carry no
  // meta).
  const packProjectId = meta?.project_id ?? projectId;

  const close = () => {
    setShowPack(false);
    setCheckoutActive(false);
    hidePaywall();
  };

  const targetPath = (target: string | null): string | null => {
    if (!target) return null;
    switch (target) {
      case 'projects':
        return '/';
      // The standalone Views/angles library was merged into the Brief page —
      // route the server-driven angle-library target there.
      case 'project_angles':
        return projectId ? `/project/${projectId}/goals` : null;
      // References folded into the Brief page (Phase 2) — route the
      // server-driven reference target there.
      case 'project_references':
        return projectId ? `/project/${projectId}/goals` : null;
      default:
        return null;
    }
  };

  const handleAction = (action: PaywallAction) => {
    // A bulletless purchase_pack (the render-queue limit) renders as the small
    // alert; its CTA swaps THIS dialog to the full pack body in place rather
    // than closing — the alert gave the context the pack body lacks. Rich pack
    // offers (with bullets) render the pack body directly and never call this.
    if (action.kind === 'purchase_pack') {
      setShowPack(true);
      return;
    }
    if (action.kind === 'open_pass_paywall') {
      // Pass upsell for a Pass-liftable limit. Server `target` is the
      // entitlement key; the store highlight uses the short form. The
      // `pro_plan` target opens the Pro Plan sheet; `pro_pass` opens the
      // Tokens Pro Pass. An UNRECOGNIZED target is a no-op (fail-safe: never
      // guess a paywall the server didn't ask for) — kept aligned across
      // Apple/Android/Web.
      const highlight =
        action.target === 'pro_plan'
          ? 'pro_plan'
          : action.target === 'pro_pass'
            ? 'pro'
            : null;
      if (highlight) {
        // Hand off to the Pass sheet WITHOUT closing this dialog's store
        // state first — pass `returnTo` so its back button can restore this
        // exact payload. We still don't render both Dialogs open at once
        // (see the `open` call below): two stacked modal Dialogs each paint
        // their own translucent overlay, and having both mounted open
        // visibly doubles the dim. So we close this dialog's own state here
        // and let the Pass store carry what to reopen on back.
        if (payload && code) {
          openPassPaywall(highlight, { payload, code, meta });
        } else {
          openPassPaywall(highlight);
        }
      }
      close();
      return;
    }
    close();
    switch (action.kind) {
      case 'purchase_tokens':
        // INSUFFICIENT_TOKENS renders the token package cards inline instead
        // (see below) — this branch is the fallback for any other payload
        // that still ships a bare purchase_tokens action.
        router.push('/account/tokens');
        break;
      case 'delete_existing': {
        const path = targetPath(action.target);
        if (path) router.push(path);
        break;
      }
      case 'dismiss':
      default:
        // Unknown future action kinds degrade to dismiss — the catalog is
        // server-driven and a new kind may ship before this client maps it.
        break;
    }
  };

  // Hide the secondary action only when it would re-route to the screen the
  // user is already on (mostly `delete_existing` with a target). For
  // `purchase_tokens` we always show it — the destination is implicit and the
  // user came here specifically to act on it.
  const showSecondary = (() => {
    const action = payload?.secondary_action;
    if (!action) return false;
    if (action.kind === 'delete_existing') {
      const path = targetPath(action.target);
      if (!path) return false;
      return pathname !== path;
    }
    return true;
  })();

  // A RICH pack offer (purchase_pack carrying bullets — PACK_REQUIRED /
  // FREE_DESIGNS_EXHAUSTED) renders the pack body directly. A bulletless
  // purchase_pack (the render-queue limit) stays the small alert until the user
  // taps its CTA (`showPack`) — then it swaps to the canonical pack body so the
  // pitch isn't the alert's parallel-renders context line.
  const isRichPackOffer =
    payload?.primary_action?.kind === 'purchase_pack' &&
    (payload.bullets?.length ?? 0) > 0;
  const showPackBody = !!payload && (isRichPackOffer || showPack);
  const packBodyPayload = isRichPackOffer
    ? payload
    : (packRequiredPayload ?? payload);

  // INSUFFICIENT_TOKENS renders a row of token-package cards instead of the
  // generic footer buttons — each card launches its own Paddle checkout
  // directly, no navigation to /account/tokens (parity with Apple/Android,
  // which both purchase in-sheet).
  const isTokenPurchaseOffer =
    !showPackBody && payload?.primary_action?.kind === 'purchase_tokens';

  // A "product choice" payload (Hi-Res / Extended thinking / Model picker /
  // Cost estimate) ships NO primary_action and three co-equal
  // alternative_actions (Pro Pass / Facade Kit / Pro Plan) — any one of them
  // unlocks the feature. There's no separate CTA to show in the footer (the
  // three cards below ARE the choice), and no token cards above them to
  // divide from with an "or".
  const isProductChoiceOffer =
    !showPackBody &&
    !payload?.primary_action &&
    (payload?.alternative_actions?.length ?? 0) > 0;

  return (
    <Drawer
      open={payload !== null}
      // Relaxed to non-modal only while the Paddle overlay is up, so its
      // body-level iframe stays clickable (see `checkoutActive`).
      modal={!checkoutActive}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      {/* Exact Apple paywall-sheet base color (`cardElevated`: light #F7F8FB,
          dark #2C2C2E) so the sheet tone matches the iOS sheet 1:1 (the generic
          `bg-panel` #F2F2F7 read a shade grayer next to Apple). The
          alternative-product tiles are border-only (no fill), the same as Apple
          (hairline stroke) and Android (outline), so they read as one system
          across platforms rather than white cards on gray. */}
      <DrawerContentFullScreen className="bg-[#f7f8fb] dark:bg-[#2c2c2e]">
        {/* The Facade Kit pack body dismisses via a top-right close (X) — its own
            footer "dismiss" button was removed (parity with Apple/Android, which
            close the pack sheet with a corner X). The token-package offer also
            keeps only the corner X (the cards themselves are the actions, no
            footer button to duplicate). Every other payload keeps its in-footer
            dismiss/affirmative button and no corner X. */}
        {(showPackBody || isTokenPurchaseOffer || isProductChoiceOffer) && (
          <DrawerClose
            aria-label={tCommon('close')}
            className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">{tCommon('close')}</span>
          </DrawerClose>
        )}
        {/* Own scroll container inside the fixed-height sheet (DrawerContentFullScreen's
            outer wrapper is `overflow-hidden` by design — it fixes the sheet height,
            this div is what actually scrolls). `px-4`/`sm:px-6` keeps mobile padding
            close to the native platforms (Apple/Android use a narrower side inset
            than the old `p-6`/24px Dialog default). */}
        <div className="flex h-full flex-col gap-2 overflow-y-auto overscroll-contain px-4 pt-12 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-6">
          {/* Facade Pack offers get the dedicated pack body: server copy +
              building chrome + reference price + direct-to-app CTA. Every other
              payload (including the info-only PACK_*_EXHAUSTED caps) keeps the
              generic limit layout. */}
          {showPackBody && packBodyPayload ? (
            <PackPaywallContent
              payload={packBodyPayload}
              projectId={packProjectId ?? undefined}
              onDismiss={close}
              onCheckoutOpen={() => setCheckoutActive(true)}
              onCheckoutClose={() => setCheckoutActive(false)}
              // Only a bulletless offer got here by swapping THIS dialog's body
              // in place (`showPack`) — it has a parent state to go back to. A
              // rich offer IS the entry point (no parent), so it keeps just the
              // corner X.
              onBack={!isRichPackOffer ? () => setShowPack(false) : undefined}
            />
          ) : payload ? (
            <>
              <DrawerHeader className="px-0">
                {/* One step up from the base sheet title (`text-sheet-title`,
                    18px) — mirrors the size `PackPaywallContent` already uses
                    for its own headline (`text-screen-subtitle`, 24px/bold), so
                    every paywall body in this dialog family reads at the same
                    weight. */}
                {/* Header title sits BELOW the product-name tiles in the visual
                    hierarchy: the tile names are the hero (bold, `text-token-balance`
                    24/28px), so the dialog header is smaller and medium-weight —
                    `text-card-title` (18/20px) `font-medium`. Mirrors Apple
                    (23pt medium header vs 28pt bold tile) and Android (22sp
                    medium header vs 28sp bold tile). */}
                <DrawerTitle className="text-card-title font-medium">
                  {payload.title}
                </DrawerTitle>
                {/* `max-w-[80%]` caps the body at 80% of the dialog content
                    width so the paragraph balances and the last line doesn't
                    strand a single orphan word. DrawerDescription is already
                    left-aligned by default — mirrors Apple/Android.
                    `text-card-body` overrides the default `text-form-help` so
                    the subtitle matches the bullet text size (both 16px), in
                    parity with Apple/Android (both `cardBody`). */}
                <DrawerDescription className="text-card-body max-w-[80%] leading-[1.3]">
                  {payload.body}
                </DrawerDescription>
              </DrawerHeader>
              {/* Server-driven benefit bullets (e.g. INSUFFICIENT_TOKENS carries
                  3) — rendered above the actions; absent on most payloads.
                  `mt-3` keeps a light break under the title+subtitle header
                  without the wide gap the old `mt-6` left. */}
              <PaywallBullets
                bullets={payload.bullets}
                className="mt-3"
                iconClassName="text-muted-foreground"
              />
              {/* Paddle will not sell to this visitor (server-decided from their
                  real IP). The server ships this line on EVERY purchase-offering
                  paywall, so rendering it here — above the CTAs, whatever this
                  paywall happens to sell — covers all of them at once. This is
                  the sheet a buyer reaches after spending renders; without it
                  they tapped buy and got Paddle's E-403 with no way forward. */}
              {payload.web_payment_notice ? (
                <NoticeBanner
                  variant="warning"
                  message={payload.web_payment_notice}
                  className="mt-3"
                />
              ) : null}
              {isTokenPurchaseOffer ? (
                <TokenPackageOffer
                  onCheckoutOpen={() => setCheckoutActive(true)}
                  onCheckoutClose={() => setCheckoutActive(false)}
                />
              ) : isProductChoiceOffer ? null : payload.primary_action ? (
                <DrawerFooter className="flex-row justify-end gap-2 px-0">
                  <Button
                    variant="ghost"
                    onClick={() => handleAction(payload.dismiss_action)}
                  >
                    {payload.dismiss_action.label}
                  </Button>
                  {showSecondary && payload.secondary_action && (
                    <Button
                      variant="outline"
                      onClick={() => handleAction(payload.secondary_action!)}
                    >
                      {payload.secondary_action.label}
                    </Button>
                  )}
                  <Button onClick={() => handleAction(payload.primary_action!)}>
                    {payload.primary_action.label}
                  </Button>
                </DrawerFooter>
              ) : (
                // Info-only paywall: the limit can't be relieved by an action
                // here (daily/monthly render caps, or already on the top Pass).
                // The single dismiss is styled as the affirmative, full-width
                // primary button — Apple-HIG single-action sheet. No CTA.
                <DrawerFooter className="px-0">
                  <Button
                    className="w-full"
                    onClick={() => handleAction(payload.dismiss_action)}
                  >
                    {payload.dismiss_action.label}
                  </Button>
                </DrawerFooter>
              )}
              {/* Multi-product alternatives below the primary CTA (token top-up):
                  "unlock this building forever" (Facade Kit → purchase_pack, which
                  swaps this dialog to the pack body in place) and the contractor
                  Pro Plan (open_pass_paywall). Server ships these only on the
                  INSUFFICIENT_TOKENS payload; empty/absent elsewhere. Each routes
                  through the same handleAction as primary/secondary. */}
              {(payload.alternative_actions?.length ?? 0) > 0 && (
                <div className="mt-3 flex flex-col gap-2 pb-4">
                  {/* Small centered "or" divider between the token top-up
                      options above and the alternative products below — makes
                      the two option groups read as distinct choices. Only
                      shown when there's actually a group above to divide
                      from (the token cards) — a product-choice payload has
                      no primary group, the three cards ARE the whole choice. */}
                  {isTokenPurchaseOffer && (
                    <div className="flex items-center gap-3 py-1">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-form-help text-muted-foreground">
                        {tCommon('or')}
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  )}
                  {payload.alternative_actions!.map((action, i) => (
                    <AlternativeActionCard
                      key={`${action.kind}-${i}`}
                      action={action}
                      projectId={packProjectId ?? undefined}
                      onClick={() => handleAction(action)}
                    />
                  ))}
                  {/* Trust microcopy — web checks out via Paddle (paddle twin of
                      the Apple/Android secure-payment line). Server-driven;
                      rendered only when non-empty. */}
                  {payload.secure_payment_paddle ? (
                    <p className="text-card-meta text-muted-foreground flex items-center justify-center gap-1 pt-1">
                      <Lock className="h-3 w-3" aria-hidden="true" />
                      {payload.secure_payment_paddle}
                    </p>
                  ) : null}
                  {/* Terms footer — parity with the Apple/Android sheet footer
                      ("Restore purchases · Terms of service") and the pack/pass
                      sheets, shown on every purchase offer here (token top-up
                      and product choice alike). No "Restore" on web (Paddle
                      entitlements are account-bound server-side, so there's
                      nothing device-local to restore). */}
                  <div className="flex items-center justify-center pt-1">
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-card-meta text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {tLegal('support.termsOfService')}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </DrawerContentFullScreen>

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent
          showCloseButton
          className="max-h-[85vh] max-w-2xl overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>{tLegal('support.termsOfService')}</DialogTitle>
          </DialogHeader>
          <LegalDocumentPage type="terms" />
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}

/**
 * Token-package cards for the INSUFFICIENT_TOKENS payload — replaces the
 * generic dismiss/primary footer buttons entirely: each card launches its own
 * Paddle checkout directly (no navigation to /account/tokens), mirroring the
 * Apple/Android in-sheet purchase flow.
 */
function TokenPackageOffer({
  onCheckoutOpen,
  onCheckoutClose,
}: {
  // Relayed to each card so launching a token checkout relaxes THIS dialog's
  // modal body-lock — otherwise Paddle's body-level overlay renders under the
  // lock and is unclickable (same relaxation the pack/pass CTAs already do).
  onCheckoutOpen: () => void;
  onCheckoutClose: () => void;
}) {
  const { data: packages } = useTokenPackages();

  if (packages === undefined) {
    return (
      <div className="mt-3 flex flex-col gap-3">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  // No region notice here: the payload carries it and the dialog renders it once
  // above, whatever the paywall sells.
  return (
    <div className="mt-3 flex flex-col gap-3">
      {packages
        .filter((pkg) => !pkg.attributes.is_hidden_by_default)
        .map((pkg) => (
          <TokenPackageCard
            key={pkg.id}
            package={pkg}
            onCheckoutOpen={onCheckoutOpen}
            onCheckoutClose={onCheckoutClose}
          />
        ))}
    </div>
  );
}

/**
 * One alternative-product card (Facade Kit / Pro Plan / Tokens Pro Pass) shown
 * under the token top-up offer. Product name + price head the card (bold,
 * larger); the server `action.label` (already localized business copy, e.g.
 * "Unlock this building forever") sits below as the description; a trailing
 * chevron marks the row as navigable. Name/price come from the same catalog
 * sources the dedicated storefronts already use — never invented locally.
 */
function AlternativeActionCard({
  action,
  projectId,
  onClick,
}: {
  action: PaywallAction;
  projectId: string | undefined;
  onClick: () => void;
}) {
  if (action.kind === 'purchase_pack') {
    return (
      <PackAlternativeCard
        label={action.label}
        productName={action.product_name}
        projectId={projectId}
        onClick={onClick}
      />
    );
  }
  if (action.kind === 'open_pass_paywall') {
    return <PassAlternativeCard action={action} onClick={onClick} />;
  }
  // Fallback for any future alternative-action kind the client doesn't map
  // yet — plain server-driven label, no invented name/price header.
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-form-help flex w-full items-center justify-between gap-3 rounded-xl border border-border p-4 text-left transition-colors hover:bg-accent/40"
    >
      <span>{action.label}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-primary" />
    </button>
  );
}

/** Shared row chrome for the alternative-product cards. */
function AlternativeCardShell({
  name,
  price,
  description,
  onClick,
}: {
  name: string;
  price: string | null;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 rounded-xl border border-foreground/20 p-4 text-left transition-colors hover:bg-accent/40"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-token-balance truncate text-foreground">
          {name}
        </span>
        <span className="text-form-help text-muted-foreground truncate">
          {description}
        </span>
      </div>
      <div className="flex max-w-[40%] shrink-0 items-center gap-2">
        {/* Muted/secondary and semibold (never bold-and-dark) — matches
            Android (`onSurfaceVariant`) and Apple (`.secondary`) so the
            price reads the same neutral weight on every platform. */}
        {price && (
          <span className="text-card-body min-w-0 whitespace-normal text-right font-semibold text-muted-foreground">
            {price}
          </span>
        )}
        {/* Teal — matches Android/Apple chevron color across all paywall
            cards (never the neutral gray of a plain disclosure indicator). */}
        <ChevronRight className="h-5 w-5 shrink-0 text-primary" />
      </div>
    </button>
  );
}

/** Facade Kit card — name + reference price from the same GET /v1/store
 *  catalog `PackPaywallContent` uses for the offer card. */
function PackAlternativeCard({
  label,
  productName,
  projectId,
  onClick,
}: {
  label: string;
  productName?: string | null;
  projectId: string | undefined;
  onClick: () => void;
}) {
  const t = useTranslations('packs');
  const { data: catalog } = useStoreCatalog({ projectId, enabled: true });
  const product = findPackProduct(catalog);
  // Server-supplied short tile title overrides the catalog name.
  const name = productName ?? product?.attributes.name ?? t('packOfferTitle');
  const price = product ? formatPackPrice(product.attributes) : null;

  return (
    <AlternativeCardShell
      name={name}
      price={price}
      description={label}
      onClick={onClick}
    />
  );
}

/** Pro Plan / Tokens Pro Pass card — name + price from the same GET /v1/passes
 *  catalog PassPaywallDialog/PassCard use. */
function PassAlternativeCard({
  action,
  onClick,
}: {
  action: PaywallAction;
  onClick: () => void;
}) {
  const t = useTranslations('packs');
  const tCheckout = useTranslations('subscriptionCheckout');
  const isProPlan = action.target === 'pro_plan';
  const entitlementKey = isProPlan ? 'pro_plan' : 'pro_pass';
  const { data: passes = [] } = usePasses({ enabled: true });
  const pass = passes.find(
    (p) => p.attributes.entitlement_key === entitlementKey
  );
  const name = action.product_name ?? pass?.attributes.name ?? t('proPlanStatus');
  const price = pass
    ? isProPlan
      ? `${formatPackPrice(pass.attributes)}${tCheckout('pricingSummary.perMonth')}`
      : formatPackPrice(pass.attributes)
    : null;

  return (
    <AlternativeCardShell
      name={name}
      price={price}
      description={action.label}
      onClick={onClick}
    />
  );
}
